import { memo } from 'react';

import { CheckIcon, CircleAlertIcon, ClockIcon, XIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';

import { type WorkspaceAgentPhaseStatus } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/types';

export const WORKSPACE_AGENT_CANCELLED_PHASE_TOOLTIP = 'This step was stopped before it finished.';

const STATUS_LABEL: Record<WorkspaceAgentPhaseStatus, string> = {
  pending: 'Pending',
  running: 'In progress',
  done: 'Done',
  awaitingApproval: 'Awaiting approval',
  error: 'Failed',
  cancelled: 'Cancelled',
};

const renderStatusGlyph = (status: WorkspaceAgentPhaseStatus) => {
  switch (status) {
    case 'pending': {
      return <span aria-hidden className="size-1 rounded-full ads:bg-ads-gray-8 bg-gray-8" />;
    }
    case 'running': {
      return <span aria-hidden className="size-1 animate-pulse rounded-full ads:bg-ads-gray-8 bg-gray-8" />;
    }
    case 'done': {
      return <CheckIcon className="ads:text-ads-grass-9 text-grass-9" size="xs" />;
    }
    case 'awaitingApproval': {
      return <ClockIcon className="ads:text-icon-primary text-altitude-9" size="xs" />;
    }
    case 'error': {
      return <CircleAlertIcon className="text-flash-9" size="xs" />;
    }
    case 'cancelled': {
      return <XIcon className="text-gray-9" size="xs" />;
    }
  }
};

export type WorkspaceAgentPhaseStatusIconProps = {
  'data-testid'?: string;
  status: WorkspaceAgentPhaseStatus;
};

/**
 * Status for one phase or tool call. Owns its own fixed-size slot so a list of these stays aligned
 * as rows change status and the glyphs differ in size.
 *
 * The status word is announced rather than left to the glyph. Sighted users read the difference
 * between a pulsing dot and a tick; without this a screen reader gets a bare label and no state.
 */
export const WorkspaceAgentPhaseStatusIcon = memo(
  ({ 'data-testid': testId, status }: WorkspaceAgentPhaseStatusIconProps) => {
    if (status !== 'cancelled') {
      return (
        <span className="flex size-3 shrink-0 items-center justify-center" data-testid={testId}>
          {renderStatusGlyph(status)}
          <span className="sr-only">{STATUS_LABEL[status]}</span>
        </span>
      );
    }

    return (
      <Tooltip delayDuration={0} label={WORKSPACE_AGENT_CANCELLED_PHASE_TOOLTIP} side="right" sideOffset={4}>
        <button
          aria-label={WORKSPACE_AGENT_CANCELLED_PHASE_TOOLTIP}
          className="relative flex size-3 shrink-0 cursor-default items-center justify-center rounded-2 text-gray-9 before:absolute before:-inset-1 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altitude-8"
          data-testid={testId}
          type="button"
        >
          <XIcon aria-hidden size="xs" />
        </button>
      </Tooltip>
    );
  },
);

WorkspaceAgentPhaseStatusIcon.displayName = 'WorkspaceAgentPhaseStatusIcon';
